// src/pages/lms/LMSAssignments.jsx — COMPLETE
// Fixes: assignments show correctly, class targeting, submissions viewer, grading
import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import {
  notifyNewAssignment,
  notifyGradePublished,
} from '../../services/NotificationService';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { Plus, X, Search, Trash2, Upload, FileText, Clock, Eye, ChevronDown, Star } from 'lucide-react';

const INP = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };
const F = ({ label, children, col }) => (
  <div style={{ gridColumn: col||'span 1' }}>
    <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
    {children}
  </div>
);

// ── Grade Modal ────────────────────────────────────────
function GradeModal({ sub, assignment, onSave, onClose }) {
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const marksRef   = useRef();
  const feedRef    = useRef();
  const [grade, setGrade] = useState(sub.grade||'');

  const gradeFromMarks = (m) => {
    const pct = (Number(m)/assignment.total_marks)*100;
    if (pct>=90) return 'A+'; if (pct>=80) return 'A';
    if (pct>=70) return 'B';  if (pct>=60) return 'C';
    if (pct>=50) return 'D';  return 'F';
  };

  const save = async () => {
    const marks = Number(marksRef.current?.value);
      if (isNaN(marks)||marks<0||marks>assignment.total_marks) {
      setError(`Marks must be 0–${assignment.total_marks}`); return;
    }

    setSaving(true); setError('');

    try {
      const finalGrade = grade || gradeFromMarks(marks);

      const { error } = await supabase
        .from('lms_assignment_submissions')
        .update({
          marks_obtained: marks,
          grade:          finalGrade,
          feedback:       feedRef.current?.value?.trim()||null,
          is_graded:      true,
          graded_at:      new Date().toISOString(),
        })
        .eq('id', sub.id);

      if (error) throw error;

      try {
        await notifyGradePublished(
          [sub.student_id],
          assignment,
          {
            ...sub,
            marks_obtained: marks,
            grade: finalGrade,
          }
        );
      } catch (_) {}

      onSave('✅ Grade saved!');
    } catch(e) {
      console.error('Grade save failed:', e);
      setError(e.message || 'Failed to save grade');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:480,padding:24}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <h3 style={{margin:0,fontWeight:800,fontSize:17}}>Grade Submission</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={20} color="#6B7280"/></button>
        </div>
        {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px',marginBottom:12,fontSize:13,color:'#991B1B'}}>{error}</div>}
        <div style={{background:'#F8FAFF',borderRadius:12,padding:14,marginBottom:16}}>
          <div style={{fontWeight:800,color:'#111827'}}>{sub.student_name}</div>
          <div style={{fontSize:12,color:'#6B7280'}}>
            {sub.student_roll_number&&`Roll# ${sub.student_roll_number} · `}{sub.student_class||''}
          </div>
          {sub.submitted_at&&<div style={{fontSize:11,color:'#9CA3AF',marginTop:4}}>Submitted: {new Date(sub.submitted_at).toLocaleString()}</div>}
        </div>
        {/* View submission */}
        {sub.file_url && (
          <a href={sub.file_url} target="_blank" rel="noreferrer"
            style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',borderRadius:10,border:'1px solid #BFDBFE',background:'#EFF6FF',color:'#1D4ED8',textDecoration:'none',fontSize:13,fontWeight:600,marginBottom:14}}>
            <FileText size={15}/> View Submitted File: {sub.file_name||'File'}
          </a>
        )}
        {sub.submission_text && (
          <div style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:10,padding:12,marginBottom:14,fontSize:13,color:'#374151',lineHeight:1.6,maxHeight:120,overflowY:'auto'}}>
            {sub.submission_text}
          </div>
        )}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
          <F label={`Marks (out of ${assignment.total_marks})`}>
            <input ref={marksRef} type="number" min="0" max={assignment.total_marks}
              defaultValue={sub.marks_obtained??''}
              onChange={e=>setGrade(gradeFromMarks(e.target.value))}
              style={INP}/>
          </F>
          <F label="Grade">
            <select value={grade} onChange={e=>setGrade(e.target.value)} style={INP}>
              {['A+','A','B','C','D','F'].map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </F>
        </div>
        <F label="Feedback (optional)">
          <textarea ref={feedRef} defaultValue={sub.feedback||''} rows={3}
            placeholder="Write feedback for the student..."
            style={{...INP,resize:'vertical'}}/>
        </F>
        <div style={{display:'flex',gap:10,marginTop:16}}>
          <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#7C3AED,#4F8EF7)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer'}}>
            {saving?'Saving...':'✅ Save Grade'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Submissions Modal ──────────────────────────────────
function SubmissionsModal({ assignment, onClose }) {
  const [subs,     setSubs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [grading,  setGrading]  = useState(null);
  const [success,  setSuccess]  = useState('');

  useEffect(() => { loadSubs(); }, []);

  const loadSubs = async () => {
    setLoading(true);
    const { data } = await supabase.from('lms_assignment_submissions')
      .select('*').eq('assignment_id', assignment.id)
      .order('submitted_at', {ascending:false});
    setSubs(data||[]);
    setLoading(false);
  };

  const onGraded = async (msg) => {
    setSuccess(msg); setGrading(null);
    setTimeout(()=>setSuccess(''),3000);
    await loadSubs();
  };

  const graded   = subs.filter(s=>s.is_graded).length;
  const late     = subs.filter(s=>s.is_late).length;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:720,maxHeight:'92vh',display:'flex',flexDirection:'column'}}>
        {/* Header */}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div>
            <h3 style={{margin:0,fontWeight:800,fontSize:18,color:'#111827'}}>{assignment.title}</h3>
            <div style={{fontSize:12,color:'#6B7280',marginTop:3}}>
              {subs.length} submissions · {graded} graded · {late} late · Out of {assignment.total_marks} marks
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        {success && <div style={{padding:'10px 24px',background:'#F0FDF4',borderBottom:'1px solid #BBF7D0',fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}
        {/* Body */}
        <div style={{overflowY:'auto',flex:1,padding:20}}>
          {loading ? (
            <div style={{textAlign:'center',padding:48,color:'#9CA3AF'}}>Loading...</div>
          ) : subs.length===0 ? (
            <div style={{textAlign:'center',padding:60}}>
              <div style={{fontSize:48,marginBottom:12}}>📭</div>
              <div style={{fontSize:15,fontWeight:700,color:'#374151'}}>No submissions yet</div>
              <div style={{fontSize:13,color:'#9CA3AF',marginTop:4}}>Students haven't submitted yet</div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {subs.map(s=>(
                <div key={s.id} style={{background:s.is_graded?'#F0FDF4':'#F8FAFF',border:`1px solid ${s.is_graded?'#BBF7D0':'#E2E8F0'}`,borderRadius:14,padding:'14px 16px'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,color:'#111827',fontSize:14}}>{s.student_name}</div>
                      <div style={{fontSize:12,color:'#6B7280',marginTop:2,display:'flex',gap:10,flexWrap:'wrap'}}>
                        {s.student_roll_number&&<span>Roll# {s.student_roll_number}</span>}
                        {s.student_class&&<span>Class: {s.student_class}</span>}
                        <span>Submitted: {new Date(s.submitted_at).toLocaleString()}</span>
                        {s.is_late&&<span style={{color:'#EF4444',fontWeight:700}}>⚠️ Late</span>}
                      </div>
                      {s.is_graded&&(
                        <div style={{marginTop:6,display:'flex',gap:10,fontSize:12}}>
                          <span style={{color:'#059669',fontWeight:800}}>✅ Graded: {s.marks_obtained}/{assignment.total_marks}</span>
                          {s.grade&&<span style={{color:'#7C3AED',fontWeight:800}}>Grade: {s.grade}</span>}
                          {s.feedback&&<span style={{color:'#6B7280'}}>"{s.feedback}"</span>}
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex',gap:8,flexShrink:0}}>
                      {s.file_url&&(
                        <a href={s.file_url} target="_blank" rel="noreferrer"
                          style={{display:'flex',alignItems:'center',gap:5,padding:'7px 12px',borderRadius:8,border:'1px solid #BFDBFE',background:'#EFF6FF',color:'#1D4ED8',textDecoration:'none',fontSize:12,fontWeight:600}}>
                          <FileText size={12}/> View
                        </a>
                      )}
                      <button onClick={()=>setGrading(s)}
                        style={{display:'flex',alignItems:'center',gap:5,padding:'7px 12px',borderRadius:8,border:'none',background:s.is_graded?'#F0FDF4':'linear-gradient(135deg,#7C3AED,#4F8EF7)',color:s.is_graded?'#059669':'#fff',fontSize:12,fontWeight:700,cursor:'pointer',border:s.is_graded?'1px solid #BBF7D0':'none'}}>
                        <Star size={12}/>{s.is_graded?'Re-grade':'Grade'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {grading&&<GradeModal sub={grading} assignment={assignment} onSave={onGraded} onClose={()=>setGrading(null)}/>}
    </div>
  );
}

// ── Assignment Form Modal ──────────────────────────────
function AssignmentFormModal({ editAssignment, institution, account, classes, teachers, onSave, onClose }) {
  const [saving,     setSaving]    = useState(false);
  const [uploading,  setUploading] = useState(false);
  const [error,      setError]     = useState('');
  const [attachUrl,  setAttachUrl] = useState(editAssignment?.attachment_url||'');
  const [attachName, setAttachName]= useState(editAssignment?.attachment_name||'');
  const [targetDeg,  setTargetDeg] = useState(editAssignment?.target_degree||'all');
  const [targetYr,   setTargetYr]  = useState(editAssignment?.target_year||'');
  const [subType,    setSubType]   = useState(editAssignment?.submission_type||'file');
  const [allowLate,  setAllowLate] = useState(editAssignment?.allow_late_submission||false);
  const [isPublished,setIsPublished]=useState(editAssignment?.is_published??true);
  const [selClasses, setSelClasses]= useState(editAssignment?.target_class_ids||[]);

  const titleRef    = useRef(); const descRef     = useRef();
  const subjRef     = useRef(); const teacherRef  = useRef();
  const dueRef      = useRef(); const marksRef    = useRef();
  const passRef     = useRef(); const penaltyRef  = useRef();

  const toggleClass = (id) =>
    setSelClasses(prev => prev.includes(id) ? prev.filter(c=>c!==id) : [...prev, id]);

  const uploadFile = async (file) => {
    setUploading(true); setError('');
    try {
      const path = `${institution.id}/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.storage
        .from('lms-assignments').upload(path, file, { upsert:true });
      if (upErr) throw upErr;
      const { data:{ publicUrl } } = supabase.storage.from('lms-assignments').getPublicUrl(path);
      setAttachUrl(publicUrl); setAttachName(file.name);
    } catch(e) { setError('Upload failed: '+e.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const title   = titleRef.current?.value?.trim()||'';
    const subject = subjRef.current?.value?.trim()||'';
    const due     = dueRef.current?.value||'';
    if (!title)   { setError('Title is required');    return; }
    if (!subject) { setError('Subject is required');  return; }
    if (!due)     { setError('Due date is required'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        institution_id:        institution.id,
        title,
        subject,
        description:           descRef.current?.value?.trim()||null,
        teacher_id:            teacherRef.current?.value||null,
        due_date:              new Date(due).toISOString(),
        total_marks:           Number(marksRef.current?.value)||100,
        passing_marks:         Number(passRef.current?.value)||50,
        submission_type:       subType,
        allow_late_submission: allowLate,
        late_penalty_percent:  Number(penaltyRef.current?.value)||0,
        target_degree:         targetDeg,
        target_year:           targetYr ? Number(targetYr) : null,
        target_class_ids:      selClasses.length > 0 ? selClasses : null,
        attachment_url:        attachUrl||null,
        attachment_name:       attachName||null,
        is_published:          isPublished,
      };
      if (editAssignment) {
        const { error:e } = await supabase.from('lms_assignments').update(data).eq('id',editAssignment.id);
        if (e) throw e;
        onSave('✅ Assignment updated!');
      } else {
        const { data: newA, error:e } = await supabase.from('lms_assignments').insert(data).select().single();
        if (e) throw e;
        // Send notification
        try { await notifyNewAssignment(institution.id, newA); } catch(_){}
        onSave('✅ Assignment created & students notified!');
      }
    } catch(e) { setError(e.message||'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:660,maxHeight:'94vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,fontSize:18,color:'#111827'}}>{editAssignment?'Edit Assignment':'Create Assignment'}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B'}}>{error}</div>}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <F label="Title *" col="span 2"><input ref={titleRef} defaultValue={editAssignment?.title||''} placeholder="e.g. Data Structures Assignment 1" style={INP}/></F>
            <F label="Subject *"><input ref={subjRef} defaultValue={editAssignment?.subject||''} placeholder="e.g. Data Structures" style={INP}/></F>
            <F label="Teacher">
              <select ref={teacherRef} defaultValue={editAssignment?.teacher_id||''} style={INP}>
                <option value="">Select Teacher</option>
                {teachers.map(t=><option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </F>
            <F label="Due Date & Time *"><input ref={dueRef} type="datetime-local" defaultValue={editAssignment?.due_date?.slice(0,16)||''} style={INP}/></F>
            <F label="Total Marks"><input ref={marksRef} type="number" min="1" defaultValue={editAssignment?.total_marks||100} style={INP}/></F>
            <F label="Passing Marks"><input ref={passRef} type="number" min="0" defaultValue={editAssignment?.passing_marks||50} style={INP}/></F>
            <F label="Submission Type">
              <select value={subType} onChange={e=>setSubType(e.target.value)} style={INP}>
                <option value="file">File Upload</option>
                <option value="text">Text Only</option>
                <option value="both">File + Text</option>
              </select>
            </F>
            <F label="Target Degree">
              <select value={targetDeg} onChange={e=>setTargetDeg(e.target.value)} style={INP}>
                <option value="all">All Students</option>
                {['BS','BE','BBA','MS','MBA','PhD'].map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </F>
            <F label="Target Year">
              <select value={targetYr} onChange={e=>setTargetYr(e.target.value)} style={INP}>
                <option value="">All Years</option>
                {[1,2,3,4,5,6].map(y=><option key={y} value={y}>Year {y}</option>)}
              </select>
            </F>
            <F label="Description" col="span 2">
              <textarea ref={descRef} defaultValue={editAssignment?.description||''} rows={3}
                placeholder="Instructions and details..." style={{...INP,resize:'vertical'}}/>
            </F>
          </div>

          {/* Class targeting */}
          {classes.length > 0 && (
            <div style={{marginTop:16}}>
              <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:8}}>
                Target Specific Classes (leave all unchecked = assign to all classes)
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {classes.map(c=>{
                  const sel = selClasses.includes(c.id);
                  return (
                    <button key={c.id} onClick={()=>toggleClass(c.id)}
                      style={{padding:'6px 14px',borderRadius:8,border:`1.5px solid ${sel?'#7C3AED':'#E5E7EB'}`,background:sel?'#F5F3FF':'#F9FAFB',color:sel?'#7C3AED':'#374151',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                      {sel?'✓ ':''}{c.name}
                    </button>
                  );
                })}
              </div>
              {selClasses.length>0&&(
                <div style={{marginTop:6,fontSize:11,color:'#7C3AED',fontWeight:600}}>
                  ✅ Assigned to {selClasses.length} specific class{selClasses.length>1?'es':''} only
                </div>
              )}
            </div>
          )}

          {/* Attachment */}
          <div style={{marginTop:16}}>
            <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:8}}>Attachment</div>
            {attachUrl ? (
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8}}>
                <FileText size={16} color="#10B981"/>
                <span style={{fontSize:13,color:'#065F46',fontWeight:600,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{attachName}</span>
                <a href={attachUrl} target="_blank" rel="noreferrer" style={{color:'#4F8EF7',fontSize:11,fontWeight:700,textDecoration:'none'}}>View</a>
                <button onClick={()=>{setAttachUrl('');setAttachName('');}} style={{background:'none',border:'none',cursor:'pointer'}}><X size={14} color="#6B7280"/></button>
              </div>
            ) : (
              <label style={{cursor:uploading?'wait':'pointer',display:'block'}}>
                <input type="file" style={{display:'none'}} disabled={uploading}
                  onChange={e=>e.target.files?.[0]&&uploadFile(e.target.files[0])}/>
                <div style={{border:'2px dashed #BFDBFE',borderRadius:10,padding:'18px 20px',textAlign:'center',background:'#F0F9FF'}}>
                  {uploading
                    ? <span style={{color:'#3B82F6',fontSize:13}}>Uploading...</span>
                    : <><Upload size={22} color="#3B82EF" style={{margin:'0 auto 6px'}}/><div style={{color:'#1D4ED8',fontWeight:600,fontSize:13}}>Click to upload file</div><div style={{color:'#6B7280',fontSize:11,marginTop:3}}>PDF, DOC, DOCX, images, ZIP</div></>}
                </div>
              </label>
            )}
          </div>

          {/* Toggles */}
          <div style={{marginTop:14,display:'flex',flexDirection:'column',gap:10}}>
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#F9FAFB',borderRadius:8}}>
              <input type="checkbox" id="late_sub" checked={allowLate} onChange={e=>setAllowLate(e.target.checked)} style={{width:16,height:16}}/>
              <label htmlFor="late_sub" style={{fontSize:13,color:'#374151',fontWeight:600,cursor:'pointer'}}>Allow late submissions</label>
              {allowLate&&(
                <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:'auto'}}>
                  <input ref={penaltyRef} type="number" defaultValue={editAssignment?.late_penalty_percent||0} style={{width:60,...INP}} placeholder="%"/>
                  <span style={{fontSize:12,color:'#6B7280'}}>% penalty</span>
                </div>
              )}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#F9FAFB',borderRadius:8}}>
              <input type="checkbox" id="published" checked={isPublished} onChange={e=>setIsPublished(e.target.checked)} style={{width:16,height:16}}/>
              <label htmlFor="published" style={{fontSize:13,color:'#374151',fontWeight:600,cursor:'pointer'}}>Publish immediately (visible to students in app)</label>
            </div>
          </div>

          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
            <button onClick={save} disabled={saving}
              style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#7C3AED,#4F8EF7)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
              {saving?'Saving...':editAssignment?'Update Assignment':'Create & Notify Students'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────
export default function LMSAssignments() {
  const { account, institution } = useLMSAuth();
  const [assignments,  setAssignments]  = useState([]);
  const [teachers,     setTeachers]     = useState([]);
  const [classes,      setClasses]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [success,      setSuccess]      = useState('');
  const [error,        setError]        = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editA,        setEditA]        = useState(null);
  const [viewSubs,     setViewSubs]     = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const timer = useRef();
  const hs = v => { setSearchInput(v); clearTimeout(timer.current); timer.current=setTimeout(()=>setSearch(v),200); };

  useEffect(()=>{ if(institution?.id) loadAll(); },[institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const id = institution.id;

      // Fetch assignments — simple select, NO joins (no FK = join fails silently)
      const { data: aData, error: aErr } = await supabase
        .from('lms_assignments')
        .select('*')
        .eq('institution_id', id)
        .order('created_at', {ascending:false});

      if (aErr) { console.error('Assignments error:', aErr); setError('Load failed: '+aErr.message); setLoading(false); return; }

      // Fetch submission counts separately
      const { data: subCounts } = await supabase
        .from('lms_assignment_submissions')
        .select('assignment_id')
        .in('assignment_id', (aData||[]).map(a=>a.id));

      // Fetch teachers
      const { data: tData } = await supabase
        .from('lms_teachers')
        .select('id,full_name,designation')
        .eq('institution_id', id).eq('status','active').order('full_name');

      // Fetch classes
      const { data: cData } = await supabase
        .from('lms_classes')
        .select('id,name')
        .eq('institution_id', id)
        .neq('is_active', false)
        .order('name');

      // Fetch teacher names map
      const teacherMap = {};
      for (const t of (tData||[])) teacherMap[t.id] = t.full_name;

      // Count submissions per assignment
      const subCountMap = {};
      for (const s of (subCounts||[])) {
        subCountMap[s.assignment_id] = (subCountMap[s.assignment_id]||0)+1;
      }

      setAssignments((aData||[]).map(a => ({
        ...a,
        submissions_count: subCountMap[a.id] || a.submissions_count || 0,
        teacher_name: teacherMap[a.teacher_id] || null,
      })));
      setTeachers(tData||[]);
      setClasses(cData||[]);
    } catch(e) { console.error('Load error:',e); setError('Load failed: '+e.message); }
    finally { setLoading(false); }
  };

  const onSaved = async (msg) => {
    setSuccess(msg); setShowForm(false); setEditA(null);
    setTimeout(()=>setSuccess(''),5000);
    await loadAll();
  };

  const deleteA = async (id) => {
    if (!window.confirm('Delete this assignment and all its submissions?')) return;
    await supabase.from('lms_assignments').delete().eq('id',id);
    setSuccess('Assignment deleted'); setTimeout(()=>setSuccess(''),3000);
    await loadAll();
  };

  const togglePublish = async (a) => {
    await supabase.from('lms_assignments').update({is_published:!a.is_published}).eq('id',a.id);
    await loadAll();
  };

  const isOverdue = due => new Date(due) < new Date();
  const daysLeft  = due => {
    const diff = new Date(due) - new Date();
    const days = Math.ceil(diff/(1000*60*60*24));
    if (days<0)  return `${Math.abs(days)}d overdue`;
    if (days===0)return 'Due today';
    return `${days}d left`;
  };

  const filtered = useMemo(()=>assignments.filter(a=>{
    if (statusFilter==='active'  && (isOverdue(a.due_date)||!a.is_published)) return false;
    if (statusFilter==='overdue' && !isOverdue(a.due_date)) return false;
    if (statusFilter==='draft'   && a.is_published) return false;
    if (!search.trim()) return true;
    const q=search.toLowerCase();
    return (a.title||'').toLowerCase().includes(q)||(a.subject||'').toLowerCase().includes(q);
  }),[assignments,search,statusFilter]);

  // Stats
  const active  = assignments.filter(a=>a.is_published&&!isOverdue(a.due_date)).length;
  const overdue = assignments.filter(a=>isOverdue(a.due_date)).length;
  const graded  = assignments.filter(a=>a.is_graded).length;

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="assignments">

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Assignments</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>{assignments.length} total · {active} active · {overdue} overdue</p>
          </div>
          <button onClick={()=>{setEditA(null);setShowForm(true);}}
            style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#7C3AED,#4F8EF7)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
            <Plus size={16}/> Create Assignment
          </button>
        </div>

        {success && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}
        {error   && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#991B1B'}}>{error}<button onClick={()=>setError('')} style={{float:'right',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>}

        {/* Stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:12,marginBottom:20}}>
          {[
            {l:'Total',    v:assignments.length,                                          c:'#7C3AED',e:'📋'},
            {l:'Active',   v:active,                                                      c:'#10B981',e:'✅'},
            {l:'Overdue',  v:overdue,                                                     c:'#EF4444',e:'⚠️'},
            {l:'Graded',   v:graded,                                                      c:'#4F8EF7',e:'⭐'},
            {l:'Submissions', v:assignments.reduce((a,x)=>a+(x.submissions_count||0),0), c:'#F59E0B',e:'📤'},
          ].map(s=>(
            <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'14px 16px'}}>
              <div style={{fontSize:20,marginBottom:4}}>{s.e}</div>
              <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
              <div style={{fontSize:11,color:'#6B7280'}}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:16,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',flex:1,minWidth:200}}>
            <Search size={14} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
            <input value={searchInput} onChange={e=>hs(e.target.value)} placeholder="Search by title or subject..." style={{...INP,paddingLeft:28}}/>
          </div>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{...INP,width:'auto'}}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="overdue">Overdue</option>
            <option value="draft">Draft</option>
          </select>
          {(search||statusFilter)&&(
            <button onClick={()=>{setSearchInput('');setSearch('');setStatusFilter('');}}
              style={{padding:'8px 10px',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer'}}><X size={13}/></button>
          )}
          <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filtered.length}/{assignments.length}</span>
        </div>

        {/* List */}
        {loading ? (
          <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div>
        ) : filtered.length===0 ? (
          <div style={{padding:60,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB'}}>
            <div style={{fontSize:48,marginBottom:12}}>📋</div>
            <div style={{fontSize:15,fontWeight:700,color:'#374151',marginBottom:6}}>No assignments found</div>
            <div style={{fontSize:13,color:'#9CA3AF'}}>Create your first assignment using the button above</div>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {filtered.map(a=>{
              const over = isOverdue(a.due_date);
              const dl   = daysLeft(a.due_date);
              return (
                <div key={a.id} style={{background:'#fff',borderRadius:16,border:`1.5px solid ${over?'#FECACA':a.is_published?'#E5E7EB':'#FEF3C7'}`,overflow:'hidden'}}>
                  <div style={{padding:'16px 20px',display:'flex',alignItems:'flex-start',gap:12,justifyContent:'space-between'}}>
                    {/* Left */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
                        <span style={{fontSize:12,fontWeight:800,color:'#7C3AED',background:'#F5F3FF',padding:'3px 9px',borderRadius:5}}>{a.subject}</span>
                        {!a.is_published&&<span style={{fontSize:10,color:'#92400E',background:'#FEF3C7',padding:'2px 7px',borderRadius:4}}>📝 Draft</span>}
                        {a.is_graded&&<span style={{fontSize:10,color:'#059669',background:'#F0FDF4',padding:'2px 7px',borderRadius:4}}>⭐ Graded</span>}
                        {a.target_class_ids?.length>0&&(
                          <span style={{fontSize:10,color:'#0891B2',background:'#ECFEFF',padding:'2px 7px',borderRadius:4}}>
                            🎯 {a.target_class_ids.length} class{a.target_class_ids.length>1?'es':''}
                          </span>
                        )}
                        {a.teacher_name&&<span style={{fontSize:11,color:'#6B7280'}}>👤 {a.teacher_name}</span>}
                      </div>
                      <div style={{fontSize:16,fontWeight:800,color:'#111827',marginBottom:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.title}</div>
                      {a.description&&<div style={{fontSize:13,color:'#6B7280',marginBottom:8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.description}</div>}
                      <div style={{display:'flex',gap:14,fontSize:12,flexWrap:'wrap',alignItems:'center'}}>
                        <span style={{color:over?'#EF4444':'#F59E0B',fontWeight:700}}>⏰ {dl}</span>
                        <span style={{color:'#6B7280'}}>📊 {a.total_marks} marks</span>
                        <span style={{color:a.submissions_count>0?'#7C3AED':'#9CA3AF',fontWeight:a.submissions_count>0?700:400}}>
                          📤 {a.submissions_count} submission{a.submissions_count!==1?'s':''}
                        </span>
                        <span style={{color:'#6B7280'}}>📬 {a.submission_type}</span>
                        {a.attachment_url&&<a href={a.attachment_url} target="_blank" rel="noreferrer" style={{color:'#4F8EF7',fontSize:11,fontWeight:700,textDecoration:'none'}}>📎 File</a>}
                      </div>
                    </div>
                    {/* Actions */}
                    <div style={{display:'flex',gap:6,flexShrink:0,flexWrap:'wrap',justifyContent:'flex-end'}}>
                      <button onClick={()=>setViewSubs(a)}
                        style={{display:'flex',alignItems:'center',gap:5,padding:'7px 12px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${a.submissions_count>0?'#7C3AED':'#94A3B8'},${a.submissions_count>0?'#4F8EF7':'#94A3B8'})`,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                        <Eye size={12}/> {a.submissions_count} Submissions
                      </button>
                      <button onClick={()=>togglePublish(a)}
                        style={{padding:'7px 12px',borderRadius:8,border:`1px solid ${a.is_published?'#FED7AA':'#BBF7D0'}`,background:a.is_published?'#FFF7ED':'#F0FDF4',color:a.is_published?'#92400E':'#065F46',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                        {a.is_published?'Unpublish':'Publish'}
                      </button>
                      <button onClick={()=>{setEditA(a);setShowForm(true);}}
                        style={{padding:'7px 12px',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer',fontSize:12,fontWeight:600,color:'#374151'}}>
                        ✏️ Edit
                      </button>
                      <button onClick={()=>deleteA(a.id)}
                        style={{padding:'7px 10px',borderRadius:8,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}>
                        <Trash2 size={13} color="#EF4444"/>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modals */}
        {showForm&&(
          <AssignmentFormModal
            key={editA?.id||'new'}
            editAssignment={editA}
            institution={institution}
            account={account}
            classes={classes}
            teachers={teachers}
            onSave={onSaved}
            onClose={()=>{setShowForm(false);setEditA(null);}}/>
        )}
        {viewSubs&&(
          <SubmissionsModal
            assignment={viewSubs}
            onClose={()=>setViewSubs(null)}/>
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}